import { useState } from 'react';
import type { FaqContent } from '~/content/types';
import { defaultFaq } from '~/content/shared';
import { Section } from '~/components/layout';
import { cn } from '~/lib/cn';

/** FAQ: heading left, expandable question list right (Figma 38:6699). */
export function FaqAccordion({
  content = defaultFaq,
  /**
   * Align the two columns to the shared 9-col grid (left cols 1–4, right cols
   * 6–9) instead of the default 50/50 split, so the FAQ lines up with adjacent
   * `col-start-6`/`col-span-4` sections (e.g. the partnerships "promote" block).
   */
  gridAligned = false,
}: {
  content?: FaqContent;
  gridAligned?: boolean;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    // 200px gap to the section above (neighbour contributes ~56px, so pt = 144px)
    // and below (CtaBanner's band starts immediately, so pb = 200px).
    <Section className="!pt-36 !pb-[200px]">
      <div
        className={cn(
          gridAligned ? 'grid grid-cols-9 gap-5 gap-y-10' : 'grid gap-10 lg:grid-cols-2 lg:gap-24',
        )}>
        <div className={cn(gridAligned && 'col-span-9 lg:col-span-4')}>
          <h2 className="max-w-md text-h3 font-semibold tracking-[-0.03em] text-balance md:text-h2">
            {content.title}
          </h2>
        </div>
        <ul className={cn(gridAligned && 'col-span-9 lg:col-span-4 lg:col-start-6')}>
          {content.items.map((item, i) => {
            const open = openIndex === i;
            return (
              <li key={item.question} className={cn(i > 0 && 'border-t border-black-6')}>
                <button
                  type="button"
                  onClick={() => setOpenIndex(open ? null : i)}
                  aria-expanded={open}
                  className="flex w-full items-center justify-between gap-6 py-6 text-start">
                  <span className="text-h5 leading-snug font-semibold">{item.question}</span>
                  <svg
                    viewBox="0 0 24 24"
                    className={cn(
                      'size-6 shrink-0 text-grey-700 transition-transform',
                      open && 'rotate-45',
                    )}
                    stroke="currentColor"
                    strokeWidth="1.6"
                    fill="none">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </button>
                {open && <p className="pb-6 text-body text-grey-900">{item.answer}</p>}
              </li>
            );
          })}
        </ul>
      </div>
    </Section>
  );
}
